import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, X, ArrowRight } from "lucide-react";

const emptyForm = {
  name: "", sport: "cricket", start_date: "", end_date: "",
  entry_fee: 0, max_teams: 8, prize_pool: 0, format: "knockout",
  description: "", rules: "", banner_url: "", registration_deadline: "",
  status: "registrations_open",
};

export default function AdminTournaments() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = () => api.get("/tournaments").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = {
        ...form,
        entry_fee: parseFloat(form.entry_fee) || 0,
        max_teams: parseInt(form.max_teams) || 8,
        prize_pool: parseFloat(form.prize_pool) || 0,
      };
      if (editing?.id) payload.id = editing.id;
      await api.post("/admin/tournaments", payload);
      toast.success("Saved");
      setEditing(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => {
    if (!confirm("Delete tournament? This removes all teams and matches.")) return;
    await api.delete(`/admin/tournaments/${id}`);
    load();
  };

  return (
    <div data-testid="admin-tournaments">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-3">// Competitive · Tournaments</div>
          <h1 className="font-display text-4xl font-black uppercase tracking-tighter leading-none">Tournaments.</h1>
        </div>
        <button onClick={() => { setEditing({}); setForm(emptyForm); }} data-testid="new-tournament-btn" className="bg-brand-blue text-white px-5 py-2 text-xs uppercase font-bold tracking-widest flex items-center gap-2">
          <Plus size={12} /> New Tournament
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {items.map((t) => (
          <div key={t.id} data-testid={`admin-tournament-${t.id}`} className="border border-white/12 bg-[#131A2B] p-5">
            <div className="flex justify-between">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest brand-orange">{t.sport} · {t.format.replace("_", " ")}</div>
                <div className="font-display text-2xl uppercase font-bold mt-1">{t.name}</div>
                <div className="text-xs text-white/50 font-mono mt-2">{t.start_date} → {t.end_date}</div>
                <div className="text-xs text-white/50 mt-1">{t.team_count}/{t.max_teams} teams · Entry {formatINR(t.entry_fee)} · Prize {formatINR(t.prize_pool)}</div>
                <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-yellow-400">{t.status.replace(/_/g, " ")}</div>
              </div>
              <div className="space-y-1">
                <button onClick={() => { setEditing(t); setForm({ ...emptyForm, ...t }); }} className="p-1 text-white/60 hover:text-brand-orange"><Edit3 size={14} /></button>
                <button onClick={() => del(t.id)} className="p-1 text-white/60 hover:text-red-400"><Trash2 size={14} /></button>
              </div>
            </div>
            <Link
              to={`/admin/tournaments/${t.id}`}
              data-testid={`manage-${t.id}`}
              className="mt-4 flex items-center justify-between border-t border-white/12 pt-3 text-xs uppercase tracking-widest text-brand-blue hover:text-white"
            >
              <span>Manage · Bracket · Schedule matches</span>
              <ArrowRight size={12} />
            </Link>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setEditing(null)}>
          <div className="bg-[#131A2B] border border-white/12 p-8 max-w-2xl w-full my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-2xl uppercase font-black">{editing.id ? "Edit" : "New"} Tournament</h3>
              <button onClick={() => setEditing(null)}><X size={18} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" data-testid="t-name" className="bg-transparent border border-white/20 py-3 px-4 col-span-2" />
              <select value={form.sport} onChange={(e) => setForm({ ...form, sport: e.target.value })} className="bg-transparent border border-white/20 py-3 px-4">
                <option>cricket</option><option>football</option><option>badminton</option>
              </select>
              <select value={form.format} onChange={(e) => setForm({ ...form, format: e.target.value })} className="bg-transparent border border-white/20 py-3 px-4">
                <option value="knockout">Knockout</option>
                <option value="round_robin">Round Robin</option>
              </select>
              <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="bg-transparent border border-white/20 py-3 px-4" />
              <input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="bg-transparent border border-white/20 py-3 px-4" />
              <input type="number" value={form.max_teams} onChange={(e) => setForm({ ...form, max_teams: e.target.value })} placeholder="Max teams" className="bg-transparent border border-white/20 py-3 px-4" />
              <input type="number" value={form.entry_fee} onChange={(e) => setForm({ ...form, entry_fee: e.target.value })} placeholder="Entry fee" className="bg-transparent border border-white/20 py-3 px-4" />
              <input type="number" value={form.prize_pool} onChange={(e) => setForm({ ...form, prize_pool: e.target.value })} placeholder="Prize pool" className="bg-transparent border border-white/20 py-3 px-4 col-span-2" />
              <input value={form.banner_url} onChange={(e) => setForm({ ...form, banner_url: e.target.value })} placeholder="Banner image URL" className="bg-transparent border border-white/20 py-3 px-4 col-span-2" />
              <input type="date" value={form.registration_deadline || ""} onChange={(e) => setForm({ ...form, registration_deadline: e.target.value })} placeholder="Registration deadline" className="bg-transparent border border-white/20 py-3 px-4" />
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="bg-transparent border border-white/20 py-3 px-4">
                <option value="draft">Draft</option>
                <option value="registrations_open">Registrations Open</option>
                <option value="registrations_closed">Registrations Closed</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
              </select>
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description" rows={3} className="bg-transparent border border-white/20 py-3 px-4 col-span-2" />
              <textarea value={form.rules} onChange={(e) => setForm({ ...form, rules: e.target.value })} placeholder="Rules" rows={3} className="bg-transparent border border-white/20 py-3 px-4 col-span-2" />
            </div>
            <button onClick={save} data-testid="t-save" className="mt-6 w-full bg-brand-blue text-white py-3 font-bold uppercase tracking-widest">Save Tournament</button>
          </div>
        </div>
      )}
    </div>
  );
}
